import React, { useRef, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config.js';
import { useToast } from '../context/ToastContext.js';
import { useConfirm } from './ui/useConfirm.js';
import './EnhancedVideoPlayerV2.css';

// Tools drawn by dragging a start→end point (vs single-tap or multi-click tools).
const DRAG_TOOLS = ['circle', 'line', 'arrow', 'rectangle', 'distance', 'calibrate'];

// Pick an audio container the browser can record (Safari/iOS: mp4; Chrome: webm).
function pickAudioType() {
  const candidates = [
    { mimeType: 'audio/mp4', ext: 'm4a' },
    { mimeType: 'audio/webm;codecs=opus', ext: 'webm' },
    { mimeType: 'audio/webm', ext: 'webm' },
  ];
  const canCheck = typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported;
  for (const c of candidates) {
    if (canCheck && MediaRecorder.isTypeSupported(c.mimeType)) return c;
  }
  return { mimeType: '', ext: 'm4a' };
}

export default function EnhancedVideoPlayerV2({ videoSrc, videoId, fps = 30, poseJsonPath = null, readOnly = false }) {
  const toast = useToast();
  const [confirm, confirmModal] = useConfirm();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const poseCanvasRef = useRef(null);
  const textInputRef = useRef(null);
  const currentPath = useRef([]);
  const autoPauseTimeout = useRef(null);

  // Playback state
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1.0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  // A–B loop (drill a skill on repeat)
  const [loopA, setLoopA] = useState(null);
  const [loopB, setLoopB] = useState(null);

  // Zoom / pan (inspect fine detail)
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const panDrag = useRef(null); // { startX, startY, panX, panY }

  // Timing measurement (flight time, time-to-stabilization, block time…)
  const [markT1, setMarkT1] = useState(null);
  const [markT2, setMarkT2] = useState(null);

  // Voice note recording
  const [isRecordingAudio, setIsRecordingAudio] = useState(false);
  const audioRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  
  // Drawing state
  const [isDrawingMode, setIsDrawingMode] = useState(false);
  const [drawTool, setDrawTool] = useState('freehand'); // 'freehand', 'circle', 'line', 'arrow', 'angle', 'text'
  const [drawings, setDrawings] = useState([]); // Stores freehand + shapes
  const [textAnnotations, setTextAnnotations] = useState([]); // Stores text with timestamps
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawColor, setDrawColor] = useState('#FF0000');
  const [lineWidth, setLineWidth] = useState(3);
  // Real-world calibration for the distance tool: { unitsPerPx, unit }.
  // Set by drawing over a known length with the Calibrate tool.
  const [calibration, setCalibration] = useState(null);
  const [startPoint, setStartPoint] = useState(null);
  const [tempShape, setTempShape] = useState(null);
  
  // Text annotation state
  const [showTextInput, setShowTextInput] = useState(false);
  const [textInputPosition, setTextInputPosition] = useState({ x: 0, y: 0 });
  const [currentTextInput, setCurrentTextInput] = useState('');
  
  // Auto-pause state
  const [pausedDrawings, setPausedDrawings] = useState(new Set());
  const [isAutoPaused, setIsAutoPaused] = useState(false);
  
  // UI state
  const [showAnnotationList, setShowAnnotationList] = useState(false);
  const [showAll, setShowAll] = useState(false); // review mode: show every annotation regardless of time

  // AI 2D pose overlay
  const [poseFrames, setPoseFrames] = useState(null); // single-athlete fallback: frames of 33 [x,y,z,vis]
  const [posePersons, setPosePersons] = useState(null); // multi-person: per frame [{id,athlete,color,lm}]
  const [showPose, setShowPose] = useState(false);

  // ========== VIDEO LIFECYCLE ==========
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadedMetadata = () => {
      setDuration(video.duration);
    };

    const handleTimeUpdate = () => {
      if (!isAutoPaused) {
        const time = video.currentTime;
        // A–B loop: jump back to A the moment we reach B.
        if (loopA != null && loopB != null && loopB > loopA && time >= loopB) {
          video.currentTime = loopA;
          setCurrentTime(loopA);
          redrawCanvas(loopA);
          return;
        }
        setCurrentTime(time);
        redrawCanvas(time);
        checkForAutoPause(time);
      }
    };

    const handlePlay = () => {
      setIsPlaying(true);
    };

    const handlePause = () => {
      if (!isAutoPaused) {
        setIsPlaying(false);
      }
    };

    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);

    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
    };
  }, [isAutoPaused, drawings, textAnnotations, loopA, loopB]);

  // Set playback rate
  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  // Sync canvas size with video
  useEffect(() => {
    const syncCanvasSize = () => {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (!video || !canvas) return;

      canvas.width = video.offsetWidth;
      canvas.height = video.offsetHeight;
      if (poseCanvasRef.current) {
        poseCanvasRef.current.width = video.offsetWidth;
        poseCanvasRef.current.height = video.offsetHeight;
      }
      redrawCanvas(currentTime);
      drawPoseOverlay(currentTime);
    };

    syncCanvasSize();
    window.addEventListener('resize', syncCanvasSize);
    return () => window.removeEventListener('resize', syncCanvasSize);
  }, [videoSrc, currentTime, drawings, textAnnotations]);

  // Auto-focus text input when it appears
  useEffect(() => {
    if (showTextInput && textInputRef.current) {
      textInputRef.current.focus();
    }
  }, [showTextInput]);

  // ========== CANVAS DRAWING ==========
  const redrawCanvas = useCallback((time) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const timeWindow = 0.033; // Show drawings within 33ms (1 frame at 30fps)

    // Draw all paths/shapes at current timestamp
    drawings.forEach((drawing) => {
      if (Math.abs(drawing.timestamp - time) <= timeWindow || isDrawingMode || showAll) {
        // Scale stored coords from the canvas size they were drawn at to the current size
        ctx.save();
        ctx.scale(
          canvas.width / (drawing.baseW || canvas.width),
          canvas.height / (drawing.baseH || canvas.height)
        );
        ctx.strokeStyle = drawing.color;
        ctx.lineWidth = drawing.lineWidth;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        if (drawing.type === 'freehand') {
          // Draw freehand path
          if (drawing.points.length < 2) return;
          ctx.beginPath();
          ctx.moveTo(drawing.points[0].x, drawing.points[0].y);
          for (let i = 1; i < drawing.points.length; i++) {
            ctx.lineTo(drawing.points[i].x, drawing.points[i].y);
          }
          ctx.stroke();
        } else if (drawing.type === 'circle') {
          // Draw circle
          const radius = Math.sqrt(
            Math.pow(drawing.endX - drawing.startX, 2) +
            Math.pow(drawing.endY - drawing.startY, 2)
          );
          ctx.beginPath();
          ctx.arc(drawing.startX, drawing.startY, radius, 0, 2 * Math.PI);
          ctx.stroke();
        } else if (drawing.type === 'line') {
          // Draw straight line
          ctx.beginPath();
          ctx.moveTo(drawing.startX, drawing.startY);
          ctx.lineTo(drawing.endX, drawing.endY);
          ctx.stroke();
        } else if (drawing.type === 'arrow') {
          // Draw arrow
          const headlen = 15;
          const dx = drawing.endX - drawing.startX;
          const dy = drawing.endY - drawing.startY;
          const angle = Math.atan2(dy, dx);

          // Draw line
          ctx.beginPath();
          ctx.moveTo(drawing.startX, drawing.startY);
          ctx.lineTo(drawing.endX, drawing.endY);
          ctx.stroke();

          // Draw arrowhead
          ctx.beginPath();
          ctx.moveTo(drawing.endX, drawing.endY);
          ctx.lineTo(
            drawing.endX - headlen * Math.cos(angle - Math.PI / 6),
            drawing.endY - headlen * Math.sin(angle - Math.PI / 6)
          );
          ctx.moveTo(drawing.endX, drawing.endY);
          ctx.lineTo(
            drawing.endX - headlen * Math.cos(angle + Math.PI / 6),
            drawing.endY - headlen * Math.sin(angle + Math.PI / 6)
          );
          ctx.stroke();
        } else if (drawing.type === 'angle') {
          // Draw angle measurement (3 points)
          if (drawing.points && drawing.points.length === 3) {
            const [p1, p2, p3] = drawing.points;
            
            // Draw lines
            ctx.beginPath();
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.lineTo(p3.x, p3.y);
            ctx.stroke();

            // Calculate angle
            const angle1 = Math.atan2(p1.y - p2.y, p1.x - p2.x);
            const angle2 = Math.atan2(p3.y - p2.y, p3.x - p2.x);
            let angleDiff = Math.abs(angle2 - angle1);
            angleDiff = Math.min(angleDiff, 2 * Math.PI - angleDiff);
            const degrees = Math.round((angleDiff * 180) / Math.PI);

            // Draw angle arc
            const arcRadius = 30;
            ctx.beginPath();
            ctx.arc(p2.x, p2.y, arcRadius, angle1, angle2, angle2 < angle1);
            ctx.stroke();

            // Draw angle text
            ctx.fillStyle = drawing.color;
            ctx.font = 'bold 16px Arial';
            ctx.fillText(`${degrees}°`, p2.x + 10, p2.y - 10);
          }
        } else if (drawing.type === 'rectangle') {
          ctx.strokeRect(drawing.startX, drawing.startY, drawing.endX - drawing.startX, drawing.endY - drawing.startY);
        } else if (drawing.type === 'distance') {
          // Measurement line + length label (real units if calibrated, else px).
          ctx.beginPath();
          ctx.moveTo(drawing.startX, drawing.startY);
          ctx.lineTo(drawing.endX, drawing.endY);
          ctx.stroke();
          const pxLen = Math.hypot(drawing.endX - drawing.startX, drawing.endY - drawing.startY);
          const label = calibration
            ? `${(pxLen * calibration.unitsPerPx).toFixed(1)} ${calibration.unit}`
            : `${Math.round(pxLen)} px`;
          const mx = (drawing.startX + drawing.endX) / 2;
          const my = (drawing.startY + drawing.endY) / 2;
          ctx.font = 'bold 15px Arial';
          ctx.lineWidth = 3;
          ctx.strokeStyle = '#000';
          ctx.strokeText(label, mx + 6, my - 6);
          ctx.fillStyle = drawing.color;
          ctx.fillText(label, mx + 6, my - 6);
        } else if (drawing.type === 'vline') {
          // Plumb line (full height) for vertical-alignment coaching.
          ctx.beginPath();
          ctx.moveTo(drawing.x, 0);
          ctx.lineTo(drawing.x, drawing.baseH || canvas.height);
          ctx.stroke();
        } else if (drawing.type === 'hline') {
          // Level line (full width).
          ctx.beginPath();
          ctx.moveTo(0, drawing.y);
          ctx.lineTo(drawing.baseW || canvas.width, drawing.y);
          ctx.stroke();
        }
        ctx.restore();
      }
    });

    // Draw temp shape while dragging
    if (tempShape && isDrawing) {
      ctx.strokeStyle = drawColor;
      ctx.lineWidth = lineWidth;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      if (drawTool === 'circle' && tempShape.endX && tempShape.endY) {
        const radius = Math.sqrt(
          Math.pow(tempShape.endX - tempShape.startX, 2) +
          Math.pow(tempShape.endY - tempShape.startY, 2)
        );
        ctx.beginPath();
        ctx.arc(tempShape.startX, tempShape.startY, radius, 0, 2 * Math.PI);
        ctx.stroke();
      } else if (drawTool === 'line' && tempShape.endX && tempShape.endY) {
        ctx.beginPath();
        ctx.moveTo(tempShape.startX, tempShape.startY);
        ctx.lineTo(tempShape.endX, tempShape.endY);
        ctx.stroke();
      } else if (drawTool === 'arrow' && tempShape.endX && tempShape.endY) {
        const headlen = 15;
        const dx = tempShape.endX - tempShape.startX;
        const dy = tempShape.endY - tempShape.startY;
        const angle = Math.atan2(dy, dx);

        ctx.beginPath();
        ctx.moveTo(tempShape.startX, tempShape.startY);
        ctx.lineTo(tempShape.endX, tempShape.endY);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(tempShape.endX, tempShape.endY);
        ctx.lineTo(
          tempShape.endX - headlen * Math.cos(angle - Math.PI / 6),
          tempShape.endY - headlen * Math.sin(angle - Math.PI / 6)
        );
        ctx.moveTo(tempShape.endX, tempShape.endY);
        ctx.lineTo(
          tempShape.endX - headlen * Math.cos(angle + Math.PI / 6),
          tempShape.endY - headlen * Math.sin(angle + Math.PI / 6)
        );
        ctx.stroke();
      } else if (drawTool === 'rectangle' && tempShape.endX != null && tempShape.endY != null) {
        ctx.strokeRect(tempShape.startX, tempShape.startY, tempShape.endX - tempShape.startX, tempShape.endY - tempShape.startY);
      } else if ((drawTool === 'distance' || drawTool === 'calibrate') && tempShape.endX != null && tempShape.endY != null) {
        ctx.beginPath();
        ctx.moveTo(tempShape.startX, tempShape.startY);
        ctx.lineTo(tempShape.endX, tempShape.endY);
        ctx.stroke();
      }
    }

    // Draw text annotations
    textAnnotations.forEach((annotation) => {
      if (Math.abs(annotation.timestamp - time) <= timeWindow || isDrawingMode || showAll) {
        ctx.save();
        ctx.scale(
          canvas.width / (annotation.baseW || canvas.width),
          canvas.height / (annotation.baseH || canvas.height)
        );
        ctx.fillStyle = annotation.color;
        ctx.font = `bold ${annotation.fontSize}px Arial`;
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 3;

        // Draw text with black outline
        ctx.strokeText(annotation.text, annotation.x, annotation.y);
        ctx.fillText(annotation.text, annotation.x, annotation.y);

        // Draw timestamp label
        ctx.font = '12px Arial';
        ctx.fillStyle = '#fff';
        ctx.fillRect(annotation.x - 5, annotation.y + 5, 60, 18);
        ctx.fillStyle = '#000';
        ctx.fillText(formatTime(annotation.timestamp), annotation.x, annotation.y + 18);
        ctx.restore();
      }
    });
  }, [drawings, textAnnotations, drawColor, lineWidth, tempShape, isDrawing, drawTool, isDrawingMode, showAll, calibration]);

  // ========== AI 2D POSE OVERLAY ==========
  // MediaPipe Pose connections (body skeleton)
  const POSE_CONNECTIONS = [
    [11, 12], [11, 13], [13, 15], [12, 14], [14, 16],
    [11, 23], [12, 24], [23, 24],
    [23, 25], [25, 27], [27, 31], [24, 26], [26, 28], [28, 32],
  ];

  // Fetch pose landmarks once per clip
  useEffect(() => {
    if (!poseJsonPath) { setPoseFrames(null); setPosePersons(null); return; }
    const filename = poseJsonPath.includes('/') ? poseJsonPath.split('/').pop() : poseJsonPath;
    let cancelled = false;
    fetch(`${API_BASE_URL}/features/${filename}`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        if (cancelled) return;
        setPoseFrames(d?.landmarks || null);
        setPosePersons(d?.persons || null);
      })
      .catch(() => { if (!cancelled) { setPoseFrames(null); setPosePersons(null); } });
    return () => { cancelled = true; };
  }, [poseJsonPath]);

  // Draw the skeleton for the frame nearest the current playback time.
  // Pose frames span the clip but aren't 1:1 with video frames (some frames have
  // no detection), so map proportionally by playback position.
  const drawPoseOverlay = useCallback((time) => {
    const canvas = poseCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (!showPose || !duration) return;

    const W = canvas.width, H = canvas.height;
    const frameAt = (len) => Math.min(len - 1, Math.max(0, Math.round((time / duration) * (len - 1))));

    const drawSkeleton = (lm, color, isAthlete) => {
      ctx.globalAlpha = isAthlete ? 1 : 0.65;
      ctx.strokeStyle = color || '#00E5FF';
      ctx.lineWidth = isAthlete ? 4 : 2;
      ctx.lineCap = 'round';
      POSE_CONNECTIONS.forEach(([a, b]) => {
        const A = lm[a], B = lm[b];
        if (A && B && A[3] > 0.4 && B[3] > 0.4) {
          ctx.beginPath();
          ctx.moveTo(A[0] * W, A[1] * H);
          ctx.lineTo(B[0] * W, B[1] * H);
          ctx.stroke();
        }
      });
      ctx.fillStyle = isAthlete ? '#FFEB3B' : (color || '#FFEB3B');
      lm.forEach((p) => {
        if (p && p[3] > 0.5) {
          ctx.beginPath();
          ctx.arc(p[0] * W, p[1] * H, isAthlete ? 4 : 3, 0, 2 * Math.PI);
          ctx.fill();
        }
      });
      ctx.globalAlpha = 1;
    };

    const drawBadge = (lm, id, color, isAthlete) => {
      const head = (lm[0] && lm[0][3] > 0.3)
        ? [lm[0][0], lm[0][1]]
        : (lm[11] && lm[12] && lm[11][3] > 0.2 && lm[12][3] > 0.2)
          ? [(lm[11][0] + lm[12][0]) / 2, Math.min(lm[11][1], lm[12][1])]
          : null;
      if (!head) return;
      const x = head[0] * W;
      const y = Math.max(16, head[1] * H - 16);
      const label = isAthlete ? `★ ${id}` : `${id}`;
      ctx.font = 'bold 13px sans-serif';
      const tw = ctx.measureText(label).width;
      ctx.fillStyle = color || '#00E5FF';
      const bx = x - tw / 2 - 6, by = y - 14, bw = tw + 12, bh = 18;
      ctx.beginPath();
      if (ctx.roundRect) ctx.roundRect(bx, by, bw, bh, 4); else ctx.rect(bx, by, bw, bh);
      ctx.fill();
      ctx.fillStyle = '#000';
      ctx.fillText(label, x - tw / 2, y);
    };

    // Prefer the multi-person tracks; fall back to the single-athlete landmarks.
    if (posePersons && posePersons.length) {
      const people = posePersons[frameAt(posePersons.length)] || [];
      // bystanders first, athlete drawn last so it sits on top
      const ordered = [...people].sort((a, b) => (a.athlete === b.athlete ? 0 : a.athlete ? 1 : -1));
      ordered.forEach((pp) => { if (pp.lm) drawSkeleton(pp.lm, pp.color, pp.athlete); });
      ordered.forEach((pp) => { if (pp.lm) drawBadge(pp.lm, pp.id, pp.color, pp.athlete); });
      return;
    }

    if (poseFrames && poseFrames.length) {
      const lms = poseFrames[frameAt(poseFrames.length)];
      if (lms) drawSkeleton(lms, '#00E5FF', true);
    }
  }, [showPose, posePersons, poseFrames, duration]);

  // Re-draw the overlay whenever time / toggle / data changes
  useEffect(() => {
    drawPoseOverlay(currentTime);
  }, [currentTime, drawPoseOverlay]);

  // ========== AUTO-PAUSE LOGIC ==========
  const checkForAutoPause = (time) => {
    if (isDrawingMode) return;

    const allTimestamps = [
      ...drawings.map(d => d.timestamp),
      ...textAnnotations.map(t => t.timestamp)
    ];

    for (const timestamp of allTimestamps) {
      const timeDiff = Math.abs(time - timestamp);
      
      if (timeDiff <= 0.1 && !pausedDrawings.has(timestamp)) {
        const video = videoRef.current;
        if (video && !video.paused) {
          video.pause();
          setIsAutoPaused(true);
          setPausedDrawings(prev => new Set([...prev, timestamp]));

          // Resume after 2 seconds
          autoPauseTimeout.current = setTimeout(() => {
            setIsAutoPaused(false);
            video.currentTime = timestamp + 0.15;
            video.play().catch(err => console.error('Play error:', err));
          }, 2000);

          break;
        }
      }
    }
  };

  // ========== POINTER EVENTS (mouse + touch/Apple Pencil) ==========
  // Normalise mouse and touch events to a single {clientX, clientY}.
  const getEventClient = (e) => {
    if (e.touches && e.touches.length) return { clientX: e.touches[0].clientX, clientY: e.touches[0].clientY };
    if (e.changedTouches && e.changedTouches.length) return { clientX: e.changedTouches[0].clientX, clientY: e.changedTouches[0].clientY };
    return { clientX: e.clientX, clientY: e.clientY };
  };

  const getCanvasPoint = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const { clientX, clientY } = getEventClient(e);

    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  };

  // ===== ZOOM / PAN (inspect fine detail) =====
  const zoomBy = (delta) => {
    setZoom(z => {
      const nz = Math.min(4, Math.max(1, +(z + delta).toFixed(2)));
      if (nz === 1) setPan({ x: 0, y: 0 }); // snap back to centre at 1×
      return nz;
    });
  };
  const resetView = () => { setZoom(1); setPan({ x: 0, y: 0 }); };

  // Drag-to-pan when zoomed and NOT drawing (drawing mode owns the drag).
  const handlePanStart = (e) => {
    if (isDrawingMode || zoom <= 1) return;
    const { clientX, clientY } = getEventClient(e);
    panDrag.current = { startX: clientX, startY: clientY, panX: pan.x, panY: pan.y };
  };
  const handlePanMove = (e) => {
    if (!panDrag.current) return;
    const { clientX, clientY } = getEventClient(e);
    // Divide by zoom so the image tracks the finger at any zoom level.
    const dx = (clientX - panDrag.current.startX) / zoom;
    const dy = (clientY - panDrag.current.startY) / zoom;
    setPan({ x: panDrag.current.panX + dx, y: panDrag.current.panY + dy });
  };
  const handlePanEnd = () => { panDrag.current = null; };

  const handleMouseDown = (e) => {
    if (!isDrawingMode) return;

    const point = getCanvasPoint(e);
    
    if (drawTool === 'text') {
      // Show text input at click position
      const { clientX, clientY } = getEventClient(e);
      setTextInputPosition({ x: clientX, y: clientY });
      setShowTextInput(true);
      setStartPoint(point);
    } else if (drawTool === 'vline' || drawTool === 'hline') {
      // Reference guide: a single tap drops a full-height (plumb) or full-width
      // (level) line through the point — for body-alignment coaching.
      setDrawings(prev => [...prev, {
        type: drawTool, x: point.x, y: point.y,
        color: drawColor, lineWidth, timestamp: currentTime,
        baseW: canvasRef.current?.width, baseH: canvasRef.current?.height,
      }]);
    } else if (drawTool === 'freehand') {
      // Start freehand path
      setIsDrawing(true);
      currentPath.current = [point];
    } else if (drawTool === 'angle') {
      // Handle angle measurement (3 clicks)
      if (!startPoint) {
        setStartPoint(point);
        currentPath.current = [point];
      } else if (currentPath.current.length === 1) {
        currentPath.current.push(point);
      } else if (currentPath.current.length === 2) {
        currentPath.current.push(point);
        
        // Save angle drawing
        const newDrawing = {
          type: 'angle',
          points: [...currentPath.current],
          color: drawColor,
          lineWidth: lineWidth,
          timestamp: currentTime,
          baseW: canvasRef.current?.width,
          baseH: canvasRef.current?.height
        };
        setDrawings(prev => [...prev, newDrawing]);
        
        // Reset
        setStartPoint(null);
        currentPath.current = [];
      }
    } else {
      // Start shape (circle, line, arrow)
      setIsDrawing(true);
      setStartPoint(point);
      setTempShape({ startX: point.x, startY: point.y });
    }
  };

  const handleMouseMove = (e) => {
    if (!isDrawing || !isDrawingMode) return;

    const point = getCanvasPoint(e);

    if (drawTool === 'freehand') {
      // Add point to freehand path
      currentPath.current.push(point);
      
      // Draw line segment in real-time
      const canvas = canvasRef.current;
      if (canvas && currentPath.current.length >= 2) {
        const ctx = canvas.getContext('2d');
        ctx.strokeStyle = drawColor;
        ctx.lineWidth = lineWidth;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        
        const points = currentPath.current;
        const lastPoint = points[points.length - 2];
        const currentPoint = points[points.length - 1];
        
        ctx.beginPath();
        ctx.moveTo(lastPoint.x, lastPoint.y);
        ctx.lineTo(currentPoint.x, currentPoint.y);
        ctx.stroke();
      }
    } else if (DRAG_TOOLS.includes(drawTool)) {
      // Update temp shape
      setTempShape(prev => ({ ...prev, endX: point.x, endY: point.y }));
      redrawCanvas(currentTime);
    }
  };

  const handleMouseUp = () => {
    if (!isDrawing || !isDrawingMode) return;

    if (drawTool === 'freehand') {
      // Save freehand path
      if (currentPath.current.length > 1) {
        const newDrawing = {
          type: 'freehand',
          points: [...currentPath.current],
          color: drawColor,
          lineWidth: lineWidth,
          timestamp: currentTime,
          baseW: canvasRef.current?.width,
          baseH: canvasRef.current?.height
        };
        
        setDrawings(prev => [...prev, newDrawing]);
      }
      currentPath.current = [];
    } else if (DRAG_TOOLS.includes(drawTool)) {
      if (tempShape && tempShape.endX != null && tempShape.endY != null) {
        if (drawTool === 'calibrate') {
          // Set the pixel→real scale from a known length the coach traced.
          const pxLen = Math.hypot(tempShape.endX - tempShape.startX, tempShape.endY - tempShape.startY);
          if (pxLen > 4) {
            const input = window.prompt('Real length of this line (e.g. "30 cm", "12 in"):', '30 cm');
            const m = input && String(input).trim().match(/([\d.]+)\s*([a-zA-Z"']*)/);
            const val = m ? parseFloat(m[1]) : NaN;
            if (val > 0) {
              const unit = (m[2] || 'cm').replace(/["']/g, 'in');
              setCalibration({ unitsPerPx: val / pxLen, unit });
              toast.success(`Scale set — distances now read in ${unit}.`);
            }
          }
        } else {
          setDrawings(prev => [...prev, {
            type: drawTool,
            startX: tempShape.startX, startY: tempShape.startY,
            endX: tempShape.endX, endY: tempShape.endY,
            color: drawColor, lineWidth, timestamp: currentTime,
            baseW: canvasRef.current?.width, baseH: canvasRef.current?.height,
          }]);
        }
      }
      setTempShape(null);
      setStartPoint(null);
    }

    setIsDrawing(false);
  };

  // ========== TEXT ANNOTATION ==========
  const handleTextSubmit = () => {
    if (!currentTextInput.trim() || !startPoint) {
      setShowTextInput(false);
      setCurrentTextInput('');
      setStartPoint(null);
      return;
    }

    const newAnnotation = {
      text: currentTextInput,
      x: startPoint.x,
      y: startPoint.y,
      color: drawColor,
      fontSize: 20,
      timestamp: currentTime,
      baseW: canvasRef.current?.width,
      baseH: canvasRef.current?.height
    };

    setTextAnnotations(prev => [...prev, newAnnotation]);
    setShowTextInput(false);
    setCurrentTextInput('');
    setStartPoint(null);
  };

  const handleTextKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleTextSubmit();
    } else if (e.key === 'Escape') {
      setShowTextInput(false);
      setCurrentTextInput('');
      setStartPoint(null);
    }
  };

  // ========== PLAYBACK CONTROLS ==========
  const togglePlayPause = () => {
    const video = videoRef.current;
    if (!video) return;

    if (isPlaying) {
      video.pause();
    } else {
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch(error => {
          console.error('Error playing video:', error);
        });
      }
    }
  };

  const handleSeek = (e) => {
    const video = videoRef.current;
    if (!video) return;

    const seekTime = parseFloat(e.target.value);
    video.currentTime = seekTime;
    setCurrentTime(seekTime);
    setPausedDrawings(new Set()); // Reset pause tracking
  };

  const skipBackward = () => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = Math.max(0, video.currentTime - 0.1);
  };

  const skipForward = () => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = Math.min(duration, video.currentTime + 0.1);
  };

  // Single-frame step (pauses so you can draw precisely on one frame)
  const frameStep = (frames) => {
    const video = videoRef.current;
    if (!video) return;
    video.pause();
    const dt = frames / (fps || 30);
    const t = Math.min(Math.max(0, video.currentTime + dt), duration || video.duration || 0);
    video.currentTime = t;
    setCurrentTime(t);
    redrawCanvas(t);
  };

  const jumpToAnnotation = (timestamp) => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = timestamp;
    setPausedDrawings(new Set());
  };

  // ========== DRAWING TOOLS ==========
  const clearAllAnnotations = async () => {
    if (await confirm({
      title: 'Clear all markup?',
      message: 'Remove every drawing and text annotation on this clip?',
      confirmText: 'Clear all',
      danger: true,
    })) {
      setDrawings([]);
      setTextAnnotations([]);
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    }
  };

  const undoLast = () => {
    if (textAnnotations.length > 0) {
      setTextAnnotations(prev => prev.slice(0, -1));
    } else if (drawings.length > 0) {
      setDrawings(prev => prev.slice(0, -1));
    }
  };

  const saveAnnotations = async () => {
    if (!videoId) {
      toast.error('Cannot save: No video ID provided');
      return;
    }

    try {
      const annotationData = {
        drawings: drawings,
        textAnnotations: textAnnotations
      };

      const response = await axios.post(`${API_BASE_URL}/api/videos/${videoId}/annotations`, annotationData);
      console.log('Annotations saved:', response.data);
      toast.success('Annotations saved successfully!');
    } catch (error) {
      console.error('Error saving annotations:', error);
      console.error('Error details:', error.response?.data || error.message);
      toast.error(`Failed to save annotations: ${error.response?.data?.error || error.message}`);
    }
  };

  // Composite the current frame + pose overlay + drawings into one image and
  // send it to the athlete as feedback. Requires the R2 CORS policy (crossOrigin
  // on the video) so the frame pixels are readable.
  const sendSnapshot = async () => {
    const video = videoRef.current;
    if (!video || !videoId) return;
    try {
      video.pause();
      const nW = video.videoWidth || video.offsetWidth;
      const nH = video.videoHeight || video.offsetHeight;
      const out = document.createElement('canvas');
      out.width = nW; out.height = nH;
      const octx = out.getContext('2d');
      octx.drawImage(video, 0, 0, nW, nH);                                  // 1) frame
      if (poseCanvasRef.current) octx.drawImage(poseCanvasRef.current, 0, 0, nW, nH); // 2) pose
      if (canvasRef.current) octx.drawImage(canvasRef.current, 0, 0, nW, nH);         // 3) drawings
      const blob = await new Promise(res => out.toBlob(res, 'image/jpeg', 0.9));
      if (!blob) { toast.error('Could not capture the frame.'); return; }

      const note = window.prompt('Add a note for the athlete (optional):', '') ?? '';
      const fd = new FormData();
      fd.append('image', blob, `snapshot-${videoId}.jpg`);
      fd.append('note', note);
      await axios.post(`${API_BASE_URL}/api/videos/${videoId}/snapshot`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('📸 Snapshot sent to the athlete!');
    } catch (err) {
      if (err.name === 'SecurityError' || /tainted|insecure/i.test(err.message || '')) {
        toast.error('Snapshot blocked — the video CORS policy is not active yet for this origin.');
      } else {
        toast.error(`Snapshot failed: ${err.response?.data?.error || err.message}`);
      }
    }
  };

  // ===== VOICE NOTE (record a spoken explanation → athlete) =====
  const startVoiceNote = async () => {
    if (!videoId) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const { mimeType, ext } = pickAudioType();
      let rec;
      try { rec = new MediaRecorder(stream, mimeType ? { mimeType } : {}); }
      catch { rec = new MediaRecorder(stream); }
      audioChunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const type = rec.mimeType || mimeType || 'audio/mp4';
        const blob = new Blob(audioChunksRef.current, { type });
        const note = window.prompt('Add a text note to go with your voice note (optional):', '') ?? '';
        const fd = new FormData();
        fd.append('audio', blob, `voicenote-${videoId}.${ext}`);
        fd.append('note', note);
        try {
          await axios.post(`${API_BASE_URL}/api/videos/${videoId}/voice-note`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          toast.success('🎙️ Voice note sent to the athlete!');
        } catch (err) {
          toast.error(`Voice note failed: ${err.response?.data?.error || err.message}`);
        }
      };
      rec.start();
      audioRecorderRef.current = rec;
      setIsRecordingAudio(true);
    } catch (err) {
      toast.error(`Microphone error: ${err.message}`);
    }
  };

  const stopVoiceNote = () => {
    try { audioRecorderRef.current?.stop(); } catch (_) { /* already stopped */ }
    setIsRecordingAudio(false);
  };

  const loadAnnotations = async () => {
    if (!videoId) return;

    try {
      const response = await axios.get(`${API_BASE_URL}/api/videos/${videoId}/annotations`);
      if (response.data) {
        setDrawings(response.data.drawings || []);
        setTextAnnotations(response.data.textAnnotations || []);
      }
    } catch (error) {
      console.error('Error loading annotations:', error);
    }
  };

  // Load annotations on mount
  useEffect(() => {
    if (videoId) {
      loadAnnotations();
    }
  }, [videoId]);

  // ========== UTILITY ==========
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.floor((seconds % 1) * 10);
    return `${mins}:${secs.toString().padStart(2, '0')}.${ms}`;
  };

  const allAnnotations = [
    ...drawings.map((d, i) => ({ ...d, type: d.type || 'drawing', timestamp: d.timestamp, _src: 'draw', _idx: i })),
    ...textAnnotations.map((t, i) => ({ ...t, type: 'text', timestamp: t.timestamp, _src: 'text', _idx: i }))
  ].sort((a, b) => a.timestamp - b.timestamp);

  // Remove one specific annotation (from the list panel).
  const deleteAnnotation = (item) => {
    if (item._src === 'text') setTextAnnotations(prev => prev.filter((_, i) => i !== item._idx));
    else setDrawings(prev => prev.filter((_, i) => i !== item._idx));
  };

  // ========== RENDER ==========
  return (
    <div className="enhanced-video-player-v2">
      {confirmModal}
      <div
        className="video-container"
        onMouseLeave={() => { handleMouseUp(); handlePanEnd(); }}
        onMouseDown={handlePanStart}
        onMouseMove={handlePanMove}
        onMouseUp={handlePanEnd}
        onTouchStart={handlePanStart}
        onTouchMove={handlePanMove}
        onTouchEnd={handlePanEnd}
        style={{ cursor: (!isDrawingMode && zoom > 1) ? 'grab' : 'default', overflow: 'hidden' }}
      >
        <div
          className="media-transform"
          style={{
            position: 'relative', width: '100%', height: '100%',
            transform: `scale(${zoom}) translate(${pan.x}px, ${pan.y}px)`,
            transformOrigin: 'center center',
          }}
        >
        <video
          ref={videoRef}
          src={videoSrc}
          playsInline
          crossOrigin="anonymous"
          className="video-element"
        />
        <canvas
          ref={poseCanvasRef}
          className="pose-overlay-canvas"
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
        />
        <canvas
          ref={canvasRef}
          className={`drawing-canvas ${isDrawingMode ? 'drawing-active' : ''}`}
          style={{ touchAction: isDrawingMode ? 'none' : 'auto', pointerEvents: (isDrawingMode && !readOnly) ? 'auto' : 'none' }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onTouchStart={handleMouseDown}
          onTouchMove={handleMouseMove}
          onTouchEnd={handleMouseUp}
          onTouchCancel={handleMouseUp}
        />
        </div>
        
        {/* Text Input Popup */}
        {showTextInput && (
          <div
            className="text-input-popup"
            style={{ left: textInputPosition.x, top: textInputPosition.y }}
          >
            <input
              ref={textInputRef}
              type="text"
              value={currentTextInput}
              onChange={(e) => setCurrentTextInput(e.target.value)}
              onKeyDown={handleTextKeyPress}
              placeholder="Enter text annotation..."
              maxLength={50}
            />
            <button onClick={handleTextSubmit}>✓</button>
            <button onClick={() => setShowTextInput(false)}>✗</button>
          </div>
        )}
      </div>

      <div className="video-controls">
        {/* Playback Controls */}
        <div className="controls-row">
          <button 
            onClick={togglePlayPause}
            className="control-btn play-pause-btn"
            title={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? '⏸' : '▶️'}
          </button>

          <button
            onClick={() => frameStep(-1)}
            className="control-btn"
            title="Previous frame"
          >
            ⏮
          </button>

          <button
            onClick={skipBackward}
            className="control-btn"
            title="Back 0.1s (3 frames)"
          >
            ⏪
          </button>

          <button
            onClick={skipForward}
            className="control-btn"
            title="Forward 0.1s (3 frames)"
          >
            ⏩
          </button>

          <button
            onClick={() => frameStep(1)}
            className="control-btn"
            title="Next frame"
          >
            ⏭
          </button>

          <div className="time-display">
            {formatTime(currentTime)} / {formatTime(duration)}
          </div>

          <button
            onClick={() => setShowAll(s => !s)}
            className={`control-btn ${showAll ? 'active' : ''}`}
            title="Show all annotations at once (review mode)"
          >
            👁 {showAll ? 'All' : 'Frame'}
          </button>

          {((poseFrames && poseFrames.length > 0) || (posePersons && posePersons.length > 0)) && (
            <button
              onClick={() => setShowPose(s => !s)}
              className={`control-btn ${showPose ? 'active' : ''}`}
              title="Toggle AI pose skeleton overlay (all tracked athletes)"
            >
              🦴 {showPose ? 'Pose ON' : 'Pose'}
            </button>
          )}

          <button
            onClick={() => setShowAnnotationList(!showAnnotationList)}
            className="control-btn"
            title="Show annotation list"
          >
            📋 {allAnnotations.length}
          </button>

          {videoId && !readOnly && (
            <button
              onClick={sendSnapshot}
              className="control-btn"
              title="Send a marked-up snapshot of this frame to the athlete"
            >
              📸 Send Snapshot
            </button>
          )}
          {videoId && !readOnly && (
            <button
              onClick={isRecordingAudio ? stopVoiceNote : startVoiceNote}
              className={`control-btn ${isRecordingAudio ? 'active' : ''}`}
              style={isRecordingAudio ? { color: '#ef4444', fontWeight: 700 } : undefined}
              title="Record a voice note for the athlete"
            >
              {isRecordingAudio ? '⏹ Stop & Send' : '🎙️ Voice Note'}
            </button>
          )}
        </div>

        {/* Seek Slider */}
        <div className="controls-row">
          <input
            type="range"
            min="0"
            max={duration || 0}
            step="0.01"
            value={currentTime}
            onChange={handleSeek}
            className="seek-slider"
          />
        </div>

        {/* A–B Loop + Zoom */}
        <div className="controls-row">
          <span className="control-label">🔁 Loop:</span>
          <button
            className={`control-btn ${loopA != null ? 'active' : ''}`}
            onClick={() => setLoopA(currentTime)}
            title="Set loop start (A) at the current time"
          >
            Ⓐ {loopA != null ? formatTime(loopA) : 'Set A'}
          </button>
          <button
            className={`control-btn ${loopB != null ? 'active' : ''}`}
            onClick={() => setLoopB(currentTime)}
            title="Set loop end (B) at the current time"
          >
            Ⓑ {loopB != null ? formatTime(loopB) : 'Set B'}
          </button>
          {(loopA != null || loopB != null) && (
            <button
              className="control-btn"
              onClick={() => { setLoopA(null); setLoopB(null); }}
              title="Clear the A–B loop"
            >
              ✕ Clear
            </button>
          )}

          <span className="control-label" style={{ marginLeft: 14 }}>🔍 Zoom:</span>
          <button className="control-btn" onClick={() => zoomBy(-0.5)} disabled={zoom <= 1} title="Zoom out">➖</button>
          <span className="control-label">{zoom.toFixed(1)}×</span>
          <button className="control-btn" onClick={() => zoomBy(0.5)} disabled={zoom >= 4} title="Zoom in">➕</button>
          {zoom > 1 && (
            <button className="control-btn" onClick={resetView} title="Reset zoom & pan">Reset</button>
          )}
          {zoom > 1 && !isDrawingMode && (
            <span className="control-label" style={{ color: '#6b7280' }}>drag to pan</span>
          )}

          <span className="control-label" style={{ marginLeft: 14 }}>⏱ Timing:</span>
          <button
            className={`control-btn ${markT1 != null ? 'active' : ''}`}
            onClick={() => setMarkT1(currentTime)}
            title="Mark timing start (e.g. takeoff)"
          >
            T1{markT1 != null ? ` ${formatTime(markT1)}` : ''}
          </button>
          <button
            className={`control-btn ${markT2 != null ? 'active' : ''}`}
            onClick={() => setMarkT2(currentTime)}
            title="Mark timing end (e.g. landing) — shows elapsed time"
          >
            T2{markT2 != null ? ` ${formatTime(markT2)}` : ''}
          </button>
          {markT1 != null && markT2 != null && (
            <span className="control-label" style={{ color: '#2563eb', fontWeight: 700 }}>
              = {Math.abs(markT2 - markT1).toFixed(2)}s ({Math.round(Math.abs(markT2 - markT1) * (fps || 30))} frames)
            </span>
          )}
          {(markT1 != null || markT2 != null) && (
            <button
              className="control-btn"
              onClick={() => { setMarkT1(null); setMarkT2(null); }}
              title="Clear timing marks"
            >
              ✕
            </button>
          )}
        </div>

        {/* Playback Speed */}
        <div className="controls-row speed-controls">
          <span className="control-label">Speed:</span>
          <button
            onClick={() => setPlaybackRate(0.1)}
            className={`speed-btn ${playbackRate === 0.1 ? 'active' : ''}`}
          >
            0.1x
          </button>
          <button
            onClick={() => setPlaybackRate(0.25)}
            className={`speed-btn ${playbackRate === 0.25 ? 'active' : ''}`}
          >
            0.25x
          </button>
          <button
            onClick={() => setPlaybackRate(0.5)}
            className={`speed-btn ${playbackRate === 0.5 ? 'active' : ''}`}
          >
            0.5x
          </button>
          <button
            onClick={() => setPlaybackRate(0.75)}
            className={`speed-btn ${playbackRate === 0.75 ? 'active' : ''}`}
          >
            0.75x
          </button>
          <button
            onClick={() => setPlaybackRate(1.0)}
            className={`speed-btn ${playbackRate === 1.0 ? 'active' : ''}`}
          >
            1x
          </button>
        </div>

        {/* Drawing Tool Selection */}
        {!readOnly && (
        <div className="controls-row tool-controls">
          <button
            onClick={() => setIsDrawingMode(!isDrawingMode)}
            className={`control-btn draw-toggle ${isDrawingMode ? 'active' : ''}`}
            title="Toggle Drawing Mode"
          >
            ✏️ {isDrawingMode ? 'Drawing ON' : 'Draw'}
          </button>

          {isDrawingMode && (
            <>
              <span className="control-label">Tool:</span>
              <button
                onClick={() => setDrawTool('freehand')}
                className={`tool-btn ${drawTool === 'freehand' ? 'active' : ''}`}
                title="Freehand Drawing"
              >
                ✏️ Pen
              </button>
              <button
                onClick={() => setDrawTool('circle')}
                className={`tool-btn ${drawTool === 'circle' ? 'active' : ''}`}
                title="Circle"
              >
                ⭕ Circle
              </button>
              <button
                onClick={() => setDrawTool('line')}
                className={`tool-btn ${drawTool === 'line' ? 'active' : ''}`}
                title="Line"
              >
                ➖ Line
              </button>
              <button
                onClick={() => setDrawTool('arrow')}
                className={`tool-btn ${drawTool === 'arrow' ? 'active' : ''}`}
                title="Arrow"
              >
                ➡️ Arrow
              </button>
              <button
                onClick={() => setDrawTool('angle')}
                className={`tool-btn ${drawTool === 'angle' ? 'active' : ''}`}
                title="Angle Measurement (3 clicks)"
              >
                📐 Angle
              </button>
              <button
                onClick={() => setDrawTool('text')}
                className={`tool-btn ${drawTool === 'text' ? 'active' : ''}`}
                title="Text Annotation"
              >
                🔤 Text
              </button>
              <button
                onClick={() => setDrawTool('rectangle')}
                className={`tool-btn ${drawTool === 'rectangle' ? 'active' : ''}`}
                title="Rectangle"
              >
                ▭ Box
              </button>
              <span className="control-label" style={{ marginLeft: 6 }}>Measure:</span>
              <button
                onClick={() => setDrawTool('distance')}
                className={`tool-btn ${drawTool === 'distance' ? 'active' : ''}`}
                title="Measure distance (drag). Reads real units once calibrated."
              >
                📏 Distance
              </button>
              <button
                onClick={() => setDrawTool('vline')}
                className={`tool-btn ${drawTool === 'vline' ? 'active' : ''}`}
                title="Plumb line — tap to drop a vertical alignment guide"
              >
                │ Plumb
              </button>
              <button
                onClick={() => setDrawTool('hline')}
                className={`tool-btn ${drawTool === 'hline' ? 'active' : ''}`}
                title="Level line — tap to drop a horizontal guide"
              >
                ─ Level
              </button>
              <button
                onClick={() => setDrawTool('calibrate')}
                className={`tool-btn ${drawTool === 'calibrate' ? 'active' : ''}`}
                title="Set real-world scale: trace a known length, then enter it"
              >
                🎯 Calibrate
              </button>
              {calibration && (
                <span className="control-label" style={{ color: '#16a34a' }} title="Distances read in real units">
                  ✓ {calibration.unit}
                  <button
                    onClick={() => setCalibration(null)}
                    className="tool-btn"
                    style={{ marginLeft: 4, padding: '0 6px' }}
                    title="Clear scale"
                  >✕</button>
                </span>
              )}
            </>
          )}
        </div>
        )}

        {/* Drawing Customization */}
        {isDrawingMode && (
          <div className="controls-row drawing-options">
            <div className="color-picker-wrapper">
              <label className="control-label">Color:</label>
              <input
                type="color"
                value={drawColor}
                onChange={(e) => setDrawColor(e.target.value)}
                className="color-picker"
              />
            </div>

            <div className="line-width-wrapper">
              <label className="control-label">Size:</label>
              <select
                value={lineWidth}
                onChange={(e) => setLineWidth(Number(e.target.value))}
                className="line-width-select"
              >
                <option value="2">Thin</option>
                <option value="3">Normal</option>
                <option value="5">Thick</option>
                <option value="8">Extra Thick</option>
              </select>
            </div>

            <button
              onClick={undoLast}
              className="control-btn"
              title="Undo Last"
              disabled={drawings.length === 0 && textAnnotations.length === 0}
            >
              ↶ Undo
            </button>

            <button
              onClick={clearAllAnnotations}
              className="control-btn clear-btn"
              title="Clear All"
              disabled={drawings.length === 0 && textAnnotations.length === 0}
            >
              🗑️ Clear
            </button>

            <button
              onClick={saveAnnotations}
              className="control-btn save-btn"
              title="Save Annotations"
              disabled={drawings.length === 0 && textAnnotations.length === 0}
            >
              💾 Save
            </button>
          </div>
        )}

        {/* Drawing Tips */}
        {isDrawingMode && (
          <div className="drawing-tip">
            💡 <strong>Tip:</strong> 
            {drawTool === 'angle' && ' Click 3 points to measure an angle'}
            {drawTool === 'text' && ' Click to place text annotation'}
            {drawTool === 'freehand' && ' Click and drag to draw freehand'}
            {(drawTool === 'circle' || drawTool === 'line' || drawTool === 'arrow') && ' Click and drag to draw shape'}
            . Turn OFF drawing mode to auto-pause at annotations during playback.
          </div>
        )}

        {/* Annotation List */}
        {showAnnotationList && allAnnotations.length > 0 && (
          <div className="annotation-list">
            <div className="annotation-list-header">
              <strong>📋 Annotations ({allAnnotations.length})</strong>
              <button onClick={() => setShowAnnotationList(false)}>✗</button>
            </div>
            <div className="annotation-items">
              {allAnnotations.map((annotation, index) => (
                <div
                  key={index}
                  className="annotation-item"
                  onClick={() => jumpToAnnotation(annotation.timestamp)}
                >
                  <span className="annotation-time">{formatTime(annotation.timestamp)}</span>
                  <span className="annotation-type">
                    {annotation.type === 'text' ? `📝 ${annotation.text}` : `✏️ ${annotation.type}`}
                  </span>
                  <span className="annotation-color" style={{ background: annotation.color }}></span>
                  {!readOnly && (
                    <button
                      className="annotation-delete"
                      title="Delete this annotation"
                      style={{ marginLeft: 'auto', background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', fontWeight: 'bold' }}
                      onClick={(e) => { e.stopPropagation(); deleteAnnotation(annotation); }}
                    >✕</button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
